import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, BookOpen, Layers, UserPlus, Eye, EyeOff, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ADMIN_NAV } from '../../constants/nav';
import {
  fetchAdminCourses,
  createCourse,
  updateCourse,
  fetchAdminLessons,
  createLesson,
  updateLesson,
  grantCourseAccess,
  deleteCourse,
  formatPrice,
  accessTypeLabel,
  audienceLabels,
  AUDIENCE_OPTIONS,
  ACCESS_TYPE_OPTIONS,
  CoursePayload,
  LessonPayload,
} from '../../features/courses/api/courses';
import type { Course, Lesson } from '../../types';
import { uploadPdf } from '../../services/api/uploads';
import { ShareLesson } from '../../components/courses/ShareLesson';
import { COURSE } from '../../constants/testIds';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const inputClass =
  'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

const emptyCourse: CoursePayload = {
  title: '',
  slug: '',
  description: '',
  thumbnail_url: '',
  price: 0,
  currency: 'IDR',
  access_type: 'paid',
  access_audiences: ['purchase'] as string[],
  category: 'general',
  is_published: false,
};

const emptyLesson: LessonPayload = {
  title: '',
  description: '',
  content_type: 'video',
  video_provider: 'youtube',
  video_url: '',
  pdf_url: '',
  pdf_name: '',
  pdf_size: 0,
  allow_download: false,
  sort_order: 1,
  is_published: true,
};

export default function AdminCoursesPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState<Course | null>(null);
  const [delLoading, setDelLoading] = useState(false);

  const [courseDialog, setCourseDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [courseForm, setCourseForm] = useState<CoursePayload>(emptyCourse);
  const [savingCourse, setSavingCourse] = useState(false);

  const [lessonDialog, setLessonDialog] = useState(false);
  const [activeCourse, setActiveCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [lessonForm, setLessonForm] = useState<LessonPayload>(emptyLesson);
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [savingLesson, setSavingLesson] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(0);

  const onPickPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPdf(1);
    try {
      const res = await uploadPdf(file, setUploadingPdf);
      setLessonForm((f) => ({ ...f, pdf_url: res.url, pdf_name: res.name, pdf_size: res.size }));
      toast.success('PDF berhasil diunggah');
    } catch (err: any) {
      toast.error('Gagal mengunggah PDF', { description: err.response?.data?.detail || err.message });
    } finally {
      setUploadingPdf(0);
    }
  };

  const [grantEmail, setGrantEmail] = useState('');

  const load = () => {
    setLoading(true);
    fetchAdminCourses()
      .then(setCourses)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // ---- Course ----
  const openNewCourse = () => {
    setEditingCourse(null);
    setCourseForm(emptyCourse);
    setCourseDialog(true);
  };
  const openEditCourse = (c: Course) => {
    setEditingCourse(c);
    setCourseForm({
      title: c.title,
      slug: c.slug,
      description: c.description,
      thumbnail_url: c.thumbnail_url,
      price: c.price,
      currency: c.currency,
      access_type: c.access_type,
      access_audiences: c.access_audiences && c.access_audiences.length ? c.access_audiences : ['purchase'],
      category: c.category,
      is_published: c.is_published,
    });
    setCourseDialog(true);
  };
  const saveCourse = async () => {
    setSavingCourse(true);
    try {
      if (editingCourse) {
        await updateCourse(editingCourse.id, courseForm);
        toast.success('Course diperbarui');
      } else {
        await createCourse(courseForm);
        toast.success('Course dibuat');
      }
      setCourseDialog(false);
      load();
    } catch (e: any) {
      toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message });
    } finally {
      setSavingCourse(false);
    }
  };
  const togglePublish = async (c: Course) => {
    try {
      await updateCourse(c.id, { is_published: !c.is_published });
      toast.success(c.is_published ? 'Course di-unpublish' : 'Course dipublikasikan');
      load();
    } catch (e: any) {
      toast.error('Gagal', { description: e.response?.data?.detail || e.message });
    }
  };
  const deleteC = (c: Course) => setConfirmDel(c);
  const doDelete = async () => {
    if (!confirmDel) return;
    setDelLoading(true);
    try { await deleteCourse(confirmDel.id); toast.success('Course dihapus'); load(); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setDelLoading(false); setConfirmDel(null); }
  };

  // ---- Lessons ----
  const openLessons = async (c: Course) => {
    setActiveCourse(c);
    setLessonDialog(true);
    setShowLessonForm(false);
    setGrantEmail('');
    setLessonsLoading(true);
    try {
      const l = await fetchAdminLessons(c.id);
      setLessons(l);
    } finally {
      setLessonsLoading(false);
    }
  };
  const openNewLesson = () => {
    setEditingLesson(null);
    setLessonForm({ ...emptyLesson, sort_order: lessons.length + 1 });
    setShowLessonForm(true);
  };
  const openEditLesson = (l: Lesson) => {
    setEditingLesson(l);
    setLessonForm({
      title: l.title,
      description: l.description,
      content_type: l.content_type,
      video_provider: l.video_provider,
      video_url: l.video_url,
      pdf_url: l.pdf_url || '',
      pdf_name: l.pdf_name || '',
      pdf_size: l.pdf_size || 0,
      allow_download: !!l.allow_download,
      sort_order: l.sort_order,
      is_published: l.is_published,
    });
    setShowLessonForm(true);
  };
  const saveLesson = async () => {
    if (!activeCourse) return;
    setSavingLesson(true);
    try {
      if (editingLesson) {
        await updateLesson(activeCourse.id, editingLesson.id, lessonForm);
        toast.success('Lesson diperbarui');
      } else {
        await createLesson(activeCourse.id, lessonForm);
        toast.success('Lesson dibuat');
      }
      const l = await fetchAdminLessons(activeCourse.id);
      setLessons(l);
      setShowLessonForm(false);
      load();
    } catch (e: any) {
      toast.error('Gagal menyimpan lesson', { description: e.response?.data?.detail || e.message });
    } finally {
      setSavingLesson(false);
    }
  };
  const grant = async () => {
    if (!activeCourse || !grantEmail) return;
    try {
      await grantCourseAccess(activeCourse.id, grantEmail);
      toast.success('Akses diberikan', { description: `${grantEmail} kini memiliki course ini.` });
      setGrantEmail('');
    } catch (e: any) {
      toast.error('Gagal memberi akses', { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Courses">
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-1">Management</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Kelola Course</h2>
          </div>
          <button
            data-testid={COURSE.adminNewBtn}
            onClick={openNewCourse}
            className="bg-cyan-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Course Baru
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : courses.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-10 text-center text-white/50">
            Belum ada course. Klik "Course Baru" untuk membuat.
          </div>
        ) : (
          <div className="space-y-3" data-testid={COURSE.adminList}>
            {courses.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4"
              >
                <div className="w-11 h-11 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
                  <BookOpen className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium truncate">{c.title}</p>
                    <span
                      className={
                        c.is_published
                          ? 'text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full'
                          : 'text-[10px] uppercase tracking-wider text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full'
                      }
                    >
                      {c.is_published ? 'Published' : 'Draft'}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 px-2 py-0.5 rounded-full">
                      {audienceLabels(c.access_audiences)}
                    </span>
                  </div>
                  <p className="text-xs text-white/40 truncate">/{c.slug} · {formatPrice(c.price, c.currency)}</p>
                </div>
                <button
                  data-testid={COURSE.publishToggle}
                  onClick={() => togglePublish(c)}
                  title={c.is_published ? 'Unpublish' : 'Publish'}
                  className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors"
                >
                  {c.is_published ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                <button
                  onClick={() => openEditCourse(c)}
                  title="Edit"
                  className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors"
                >
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  data-testid="course-delete-button"
                  onClick={() => deleteC(c)}
                  title="Hapus"
                  className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5 transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  data-testid={COURSE.adminManageLessons}
                  onClick={() => openLessons(c)}
                  className="bg-white/5 text-white border border-white/10 font-medium px-4 py-2 rounded-lg transition-colors hover:bg-white/10 hover:text-cyan-400 text-sm flex items-center gap-2"
                >
                  <Layers className="w-4 h-4" strokeWidth={1.5} /> Lessons
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Course Dialog */}
      <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Edit Course' : 'Course Baru'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Judul</label>
              <input className={inputClass} data-testid="course-form-title" value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Slug (opsional)</label>
              <input className={inputClass} value={courseForm.slug} onChange={(e) => setCourseForm({ ...courseForm, slug: e.target.value })} placeholder="auto dari judul jika kosong" />
            </div>
            <div>
              <label className={labelClass}>Deskripsi</label>
              <textarea className={inputClass} rows={3} value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Thumbnail URL</label>
              <input className={inputClass} value={courseForm.thumbnail_url} onChange={(e) => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Harga</label>
                <input type="number" className={inputClass} value={courseForm.price} onChange={(e) => setCourseForm({ ...courseForm, price: Number(e.target.value) })} />
              </div>
              <div>
                <label className={labelClass}>Kategori</label>
                <input className={inputClass} value={courseForm.category} onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Filter Akses (bisa pilih lebih dari satu)</label>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="course-form-access-audiences">
                {AUDIENCE_OPTIONS.map((o) => {
                  const checked = (courseForm.access_audiences || []).includes(o.value);
                  return (
                    <label
                      key={o.value}
                      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                        checked ? 'border-cyan-400/50 bg-cyan-400/5' : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        data-testid={`course-access-${o.value}`}
                        checked={checked}
                        onChange={(e) => {
                          const cur = new Set(courseForm.access_audiences || []);
                          if (e.target.checked) cur.add(o.value);
                          else cur.delete(o.value);
                          setCourseForm({ ...courseForm, access_audiences: Array.from(cur) });
                        }}
                        className="accent-cyan-400 w-4 h-4 mt-0.5"
                      />
                      <span>
                        <span className="block text-sm text-white">{o.label}</span>
                        <span className="block text-[11px] text-white/40">{o.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {(courseForm.access_audiences || []).length === 0 && (
                <p className="text-[11px] text-amber-300/80 mt-1.5">Belum ada yang dicentang — course hanya bisa diakses lewat pembelian/grant admin.</p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={courseForm.is_published} onChange={(e) => setCourseForm({ ...courseForm, is_published: e.target.checked })} className="accent-cyan-400 w-4 h-4" />
              Publish course ini
            </label>
          </div>
          <DialogFooter>
            <button
              data-testid={COURSE.adminSaveBtn}
              onClick={saveCourse}
              disabled={savingCourse || !courseForm.title}
              className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2"
            >
              {savingCourse && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
              Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lessons Dialog */}
      <Dialog open={lessonDialog} onOpenChange={setLessonDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lessons — {activeCourse?.title}</DialogTitle>
          </DialogHeader>

          {/* Grant access utility */}
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-2">
            <label className={labelClass}>Beri akses ke member (email)</label>
            <div className="flex gap-2">
              <input className={inputClass} value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="member@email.com" data-testid="grant-email-input" />
              <button
                data-testid={COURSE.adminGrantBtn}
                onClick={grant}
                className="bg-white/5 text-white border border-white/10 font-medium px-4 py-2 rounded-lg transition-colors hover:bg-white/10 hover:text-cyan-400 text-sm flex items-center gap-2 shrink-0"
              >
                <UserPlus className="w-4 h-4" strokeWidth={1.5} /> Grant
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-white/50">{lessons.length} lesson</p>
            <button
              data-testid={COURSE.adminLessonNewBtn}
              onClick={openNewLesson}
              className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" strokeWidth={2} /> Tambah Lesson
            </button>
          </div>

          {lessonsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" strokeWidth={1.5} />
            </div>
          ) : (
            <div className="space-y-2">
              {lessons.map((l) => (
                <div key={l.id} className="flex items-center gap-3 bg-white/[0.02] border border-white/10 rounded-lg p-3">
                  <span className="text-white/40 text-sm w-6">{l.sort_order}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{l.title}</p>
                    <p className="text-xs text-white/40">{l.is_published ? 'Published' : 'Draft'} · {l.content_type === 'pdf' ? 'PDF / E-Book' : l.video_provider || 'video'}{l.content_type === 'pdf' && l.pdf_size ? ` · ${(l.pdf_size / 1048576).toFixed(1)}MB` : ''}</p>
                    {l.content_type === 'pdf' && (l.pdf_size || 0) > 15 * 1048576 && (
                      <p data-testid="lesson-pdf-size-warning" className="text-[11px] text-amber-300/80 mt-0.5">File besar — bisa lambat dibuka di HP. Pertimbangkan pecah jadi beberapa materi.</p>
                    )}
                  </div>
                  {l.content_type === 'pdf' && l.pdf_url && (
                    <a
                      data-testid="lesson-pdf-test"
                      href={l.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      title="Uji buka file PDF"
                      className="p-1.5 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors"
                    >
                      <FileText className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  )}
                  <ShareLesson courseId={activeCourse?.id || l.course_id} lessonId={l.id} title={l.title} compact />
                  <button onClick={() => openEditLesson(l)} className="p-1.5 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors">
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showLessonForm && (
            <div className="border-t border-white/10 pt-4 mt-2 space-y-3">
              <p className="text-sm font-medium text-white">{editingLesson ? 'Edit Lesson' : 'Lesson Baru'}</p>
              <div>
                <label className={labelClass}>Judul</label>
                <input className={inputClass} data-testid="lesson-form-title" value={lessonForm.title} onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Deskripsi</label>
                <textarea className={inputClass} rows={2} value={lessonForm.description} onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Tipe Materi</label>
                  <select
                    data-testid="lesson-form-content-type"
                    className={inputClass}
                    value={lessonForm.content_type}
                    onChange={(e) => setLessonForm({ ...lessonForm, content_type: e.target.value })}
                  >
                    <option value="video">Video</option>
                    <option value="pdf">PDF / E-Book</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Urutan</label>
                  <input type="number" className={inputClass} value={lessonForm.sort_order} onChange={(e) => setLessonForm({ ...lessonForm, sort_order: Number(e.target.value) })} />
                </div>
              </div>
              {lessonForm.content_type === 'pdf' ? (
                <div className="space-y-2">
                  <label className={labelClass}>File PDF (maks 50MB)</label>
                  <input
                    data-testid="lesson-form-pdf-file"
                    type="file"
                    accept="application/pdf"
                    onChange={onPickPdf}
                    className="w-full text-sm text-white/60 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-cyan-400 file:text-[#050914] file:font-medium file:cursor-pointer"
                  />
                  {uploadingPdf > 0 && uploadingPdf < 100 && (
                    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-400 transition-all" style={{ width: `${uploadingPdf}%` }} />
                    </div>
                  )}
                  {lessonForm.pdf_url && (
                    <p data-testid="lesson-form-pdf-name" className="text-xs text-emerald-400 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> {lessonForm.pdf_name || 'materi.pdf'} terunggah
                    </p>
                  )}
                  <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer pt-1">
                    <input
                      data-testid="lesson-form-allow-download"
                      type="checkbox"
                      checked={!!lessonForm.allow_download}
                      onChange={(e) => setLessonForm({ ...lessonForm, allow_download: e.target.checked })}
                      className="accent-cyan-400 w-4 h-4"
                    />
                    Izinkan member mengunduh PDF ini
                  </label>
                </div>
              ) : (
                <>
                  <div>
                    <label className={labelClass}>Video Provider</label>
                    <input className={inputClass} value={lessonForm.video_provider} onChange={(e) => setLessonForm({ ...lessonForm, video_provider: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelClass}>Video URL (embed)</label>
                    <input className={inputClass} value={lessonForm.video_url} onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })} placeholder="https://www.youtube.com/embed/..." />
                  </div>
                </>
              )}
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={lessonForm.is_published} onChange={(e) => setLessonForm({ ...lessonForm, is_published: e.target.checked })} className="accent-cyan-400 w-4 h-4" />
                Publish lesson
              </label>
              <button
                data-testid={COURSE.adminLessonSaveBtn}
                onClick={saveLesson}
                disabled={savingLesson || !lessonForm.title}
                className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2"
              >
                {savingLesson && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
                Simpan Lesson
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!confirmDel}
        title="Hapus course?"
        description={confirmDel ? `"${confirmDel.title}" beserta semua lesson & akses akan dihapus permanen.` : ''}
        loading={delLoading}
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </DashboardLayout>
  );
}
